/**
 * convertBranchToEmulator
 *
 * Converts the editor-client Branch/BranchStep shape into the CT BranchDetails
 * type expected by creation-tools-ui. The output shape mirrors the response of
 * editor-api `prepareStep` so that CT's `useLoadBranchData` push-path produces
 * identical data to what the HTTP endpoint would return.
 *
 * Character/expression resolution:
 * - Resolves step.character from the characters list (editor-client already
 *   has these via fetchCharactersByBookId). Mirrors public_preview.js:194-208.
 * - Resolves step.expression from the global expressions list
 *   (GET /v1/characters/expressions). Mirrors public_preview.js:210-212.
 * - Produces the LEGACY step.data = {properties, imageName, imageUrl} when a
 *   character is present (public_preview.js:206).
 *
 * Rules:
 * - BranchDetails is not in the CT public surface (dist/index.d.ts) so we
 *   declare a local mirror that matches creation-tools-ui/src/models/BranchDetails.ts.
 * - Server-resolved fields (iconUrl, gotoBranchTitle, modsResolved,
 *   scoreResolved, tagsResolved) are left null/undefined — the emulator
 *   degrades gracefully per the drop-on-undefined contract
 *   (editor-emulator Hard Constraint #9).
 * - The function is pure (no side-effects) and is tested in task #15.
 */

// CT's public surface exports BranchStep but not BranchDetails.
import type { BranchStep as CTBranchStep } from '@dorian/creation-tools-ui';
import type { Branch } from '../../../dorian-shared/types/branch/Branch';
import type { BranchStep as ECBranchStep, BranchStepMemoryRoll } from '../../../dorian-shared/types/branch/BranchStep';
import type { AnswerStep as ECAnswerStep } from '../../../dorian-shared/types/branch/AnswerStep';
import type { Character } from '../../../dorian-shared/types/character/Character';
import type { Audio as ECAudio } from '../../../dorian-shared/types/audio/audio';
import type { AudioStepSettingsType, SFXActionSettings, MusicActionSettings } from '../../../dorian-shared/types/audio/AudioStepSettingsType';
import { resolveStepAudios } from './resolveStepAudios';
import type { VideoSettings } from '../../../dorian-shared/types/video/video';
import type { StepCheck } from '../../pages/Book/MemoryBank/memoryBankTypes';
import {
  resolveTags,
  resolveScore,
  resolveProgressBars,
  resolveMods,
  resolveReplay,
  _resolveBranchTitle,
  type HubResolveMaps,
} from './hubResolve';

export type { HubResolveMaps };

// ---------------------------------------------------------------------------
// Local step type — CTBranchStep with nullable fields that HTTP returns as null
// ---------------------------------------------------------------------------

// HTTP public_preview.js returns null for several fields when absent.
// CT dist/models/BranchStep.d.ts declares some as non-null / literal-null because
// the server always serialises them in the normal HTTP path. In the push-model they
// may carry real values or be absent → null.  We cannot widen the CT dist type (no CT
// rebuild), so we declare a local variant that correctly reflects the push-model reality.
// The cast to CTBranchStep[] happens once at the steps[] boundary in
// convertBranchToEmulator — one boundary cast instead of two per-field casts.
type CTBranchStepNullable = Omit<CTBranchStep, 'text' | 'characterExpressionId' | 'subType' | 'characterRelationship'> & {
  text: string | null;
  characterExpressionId: number | null;
  // subType / characterRelationship are declared as literal `null` in CT dist.
  // The server (step.js:52,55) serialises them as string | null from DB columns.
  // Widened here so we can forward the real value from getAllV3 (getBranchesRaw.js:705-706).
  subType: string | null;
  characterRelationship: string | null;
  // currencyToggleSettings is not declared in CT dist at all; attached via this local type.
  // A provides it at getBranchesRaw.js:707; D at step.js:62 (step.getData() spread).
  currencyToggleSettings?: string | null;
  // speakerMemoryId: stock CT type declarations lag behind the runtime contract;
  // the field is present in the server preview payload the emulator consumes.
  // EC BranchStep carries it as number|undefined; CT converts to string (prepareStep).
  speakerMemoryId?: string;
  // The fields below are not in CT dist declarations but are part of the runtime contract
  // (present in the server preview payload the emulator consumes). Declared here so
  // assignments are type-checked rather than silenced with Record<string, unknown> casts.
  stepData?: Array<{ key: string; value: string }>;
  check?: StepCheck;
  roll?: BranchStepMemoryRoll;
  achievementId?: number;
  achievementDescription?: string;
  audio?: AudioStepSettingsType;
  // stepAudios: base shape from EC; after Audio resolve each entry gains Audio?: {...}.
  // The resolved union is declared as an intersection so the boundary cast at
  // convertBranchToEmulator steps[] is the only escape hatch.
  stepAudios?: Array<
    (SFXActionSettings | MusicActionSettings) & {
      Audio?: {
        audioUrl: string;
        userId: number;
        type: string;
        audioName: string;
        id: number;
        alias: string;
        title: string;
        duration: number;
        disabled: boolean;
        createdAt: string;
        updatedAt: string;
        // policyId is absent from the EC Audio type (AudiosProvider does not carry it);
        // it is server-only metadata and the renderer does not consume it.
      };
    }
  >;
  video?: { id: number; url: string; title: string; videoSettings?: VideoSettings };
};

// ---------------------------------------------------------------------------
// Local mirror of CT BranchDetails
// (source: creation-tools-ui/src/models/BranchDetails.ts)
// ---------------------------------------------------------------------------

export type CTBranchLocation = {
  id: number;
  title: string;
  isDefault: boolean;
  backgroundId: number;
  alias: string;
  image: {
    id: number;
    alias?: string;
    imageName?: string;
    imageUrl: string;
  };
  branches_count?: number;
  stories_count?: number;
};

export type CTBranchDetails = {
  id: number;
  uuid?: string;
  // title/description/gotoBranchId/locationId are nullable here (null = server sends null
  // when field is not set). CT's own BranchDetails declares these non-null because the server
  // always serialises them; but in push-model they may be absent → null matches HTTP etalon.
  // Widened from `string` / `number` to `string | null` / `number | null` so that ?? null
  // assignments compile without requiring a cast (task #8 parity fix).
  title: string | null;
  description: string | null;
  gotoBranchId: number | null;
  x: number;
  y: number;
  locationId: number | null;
  bgColor: null;
  pos: null | number;
  location?: CTBranchLocation;
  steps: CTBranchStep[];
  links_from?: number[];
  links_to?: number[];
  // Literal union — structurally compatible with CT's string-enum StepTypeMode.
  // CT StepTypeMode is not in CT public dist/index.d.ts, so we cannot import it.
  stepTypeMode?: 'regular' | 'elimination';
};

// Minimal expression shape returned by GET /v1/characters/expressions.
// value is typed as string (not the ExpressionValue literal union) because the
// API returns an unconstrained string; narrowing to the union here would be a
// false guarantee — the boundary cast to CTBranchStep['expression'] is the real gate.
export type CharacterExpression = {
  id: number;
  title: string;
  value: string;
  def: boolean;
  createdAt?: string;
  updatedAt?: string;
};

// EC Character type only declares the fields the editor needs for its own UI.
// At runtime the API also returns properties / imageName / imageUrl which the
// emulator relies on for the LEGACY data overwrite (public_preview.js:206).
// Declared here so the charAny cast below can be replaced with a typed reference.
type CharacterWithRuntimeFields = Character & {
  properties?: unknown;
  imageName?: string;
  imageUrl?: string;
};

// ---------------------------------------------------------------------------
// Answer transformer
// ---------------------------------------------------------------------------

// Item shapes match the slices loaded in StoryBranches/index.js loadSettings.
export type AnswerTypeItem = { id: number; title: string; value: string };
export type ModifierTypeItem = { id: number; value: string };

function transformAnswer(
  ecAnswer: ECAnswerStep,
  answerTypes: AnswerTypeItem[],
  modifierTypes: ModifierTypeItem[],
): CTBranchStep['answers'][number] {
  // Build the base object that satisfies CT's AnswerStep; extra EC-only
  // fields (eliminationResultType/Text) are carried through the cast below.

  // Numeric id normalisation: HTML <select> emits e.target.value as a string.
  // These fields must be numbers before any lookup or CT switch statement runs.
  // Rule: non-null value → Number(); null/undefined → untouched (null semantics preserved).
  // gotoBranchId: null = end-of-story in CT; must NOT be coerced to 0.
  const answerTypeIdNum = ecAnswer.answerTypeId != null ? Number(ecAnswer.answerTypeId) : ecAnswer.answerTypeId;
  const gotoBranchIdNum = ecAnswer.gotoBranchId != null ? Number(ecAnswer.gotoBranchId) : ecAnswer.gotoBranchId;

  // Resolve answerType by answerTypeId from the answerTypes list.
  // D-etalon: public_preview.js:218-222 — answer.AnswerType ? {title, value} : {}
  const foundAnswerType = answerTypes.find((t) => t.id === answerTypeIdNum);

  const base = {
    id: ecAnswer.id,
    // uuid: getAllV3 rowToAnswer.getData() explicitly excludes it (getBranchesRaw.js:774-781,
    // "Match v1 step.answers: no uuid"). Not forwarded; covered by IGNORE_PATHS skip-list.
    // answerPrice/answerTypeId/text ?? null: HTTP etalon returns null when field absent (task #8).
    // CT AnswerStep.d.ts declares these non-null. However `base` is already cast through
    // `as unknown as AnswerStep` at line below — so no per-field cast is needed here.
    // We put the honest null in the object literal and rely on the existing terminal cast.
    // KISS: avoid adding three more `as unknown as T` casts when one at the boundary suffices.
    answerPrice: ecAnswer.answerPrice ?? null,
    answerType: foundAnswerType ? { title: foundAnswerType.title, value: foundAnswerType.value } : {},
    answerTypeId: answerTypeIdNum ?? null,
    gotoBranchId: gotoBranchIdNum,
    text: ecAnswer.text ?? null,
    data: (ecAnswer.data as { key: string; value: string }[] | undefined) ?? [],
    modifiers: (ecAnswer.modifiers ?? []).map((m) => ({
      answerModifierTypeId: m.answerModifierTypeId,
      // Resolve value by answerModifierTypeId from the modifierTypes list.
      // D-etalon: public_preview.js:272 — AnswerModifierType ? AnswerModifierType.value : ""
      // '' when answerModifierTypeId has no match — mirrors public_preview.js:272 `value || ""`
      value: modifierTypes.find((t) => t.id === Number(m.answerModifierTypeId))?.value ?? '',
    })),
    traits: ecAnswer.traits ?? [],
    // Resolve requireType: A carries RequireType (PascalCase, getBranchesRaw.js:790);
    // D-etalon expects camelCase requireType {title,value} (public_preview.js:243-248).
    // AnswerStep.requires is typed as `[]` (literal empty tuple) in EC types — cast through
    // unknown so TS accepts the runtime shape that actually carries RequireType from getAllV3.
    requires: (ecAnswer.requires ?? []).map((req) => {
      const { RequireType, ...rest } = req as unknown as {
        RequireType?: { title: string; value: string };
        [key: string]: unknown;
      };
      return {
        ...rest,
        requireType: RequireType ? { title: RequireType.title, value: RequireType.value } : {},
      };
    }),
    tags: ecAnswer.tags ?? [],
    ...(ecAnswer.eliminationResultType !== undefined && {
      eliminationResultType: ecAnswer.eliminationResultType,
    }),
    ...(ecAnswer.eliminationResultText !== undefined && {
      eliminationResultText: ecAnswer.eliminationResultText,
    }),
    // requirement — conditional-display check for this answer (AnswerStep.ts:32-36).
    // Distinct from `requires` (legacy unlock list); CT reads requirement for badge/lock UI.
    ...(ecAnswer.requirement !== undefined && {
      requirement: ecAnswer.requirement,
    }),
    // alternatePrices — achievement-based alternate costs (AnswerStep.ts:45).
    ...(ecAnswer.alternatePrices !== undefined && {
      alternatePrices: ecAnswer.alternatePrices,
    }),
    // autoAchievements — achievements awarded automatically on answer selection (AnswerStep.ts:46).
    ...(ecAnswer.autoAchievements !== undefined && {
      autoAchievements: ecAnswer.autoAchievements,
    }),
  };
  // Cast: CT AnswerStep does not declare elimination fields in its d.ts, but
  // the runtime renderer ignores unknown fields; structural compatibility holds.
  return base as unknown as CTBranchStep['answers'][number];
}

// ---------------------------------------------------------------------------
// Step transformer
// ---------------------------------------------------------------------------


// Empty maps used as default when caller does not supply resolver maps.
const EMPTY_MAPS: HubResolveMaps = {
  iconMap: {},
  tagTemplateMap: {},
  variableMap: {},
  branchMap: {},
};

// IMPORTANT — MAINTENANCE NOTE:
// transformStep is the client-side equivalent of editor-api public_preview.js prepareStep().
// The server uses `...additionalData` spread (all BranchStep fields are present automatically).
// We enumerate fields manually — therefore ANY new EC BranchStep field that the emulator reads
// (in CT useChoiceMemoryCallbacks, onGoForwardStack, GameplayRendererWrapper, or any renderer)
// MUST be explicitly forwarded here. The canonical reference is public_preview.js prepareStep.
// Omitting a field here causes silent undefined in the emulator, which manifests as broken
// navigation, missing audio, silent achievement, etc. (see R1 bug series: check, roll, …).
function transformStep(
  ecStep: ECBranchStep,
  characters: Character[],
  expressions: CharacterExpression[],
  maps: HubResolveMaps = EMPTY_MAPS,
  audios: ECAudio[] = [],
  audioPath: string | null = null,
  answerTypes: AnswerTypeItem[] = [],
  modifierTypes: ModifierTypeItem[] = [],
): CTBranchStepNullable {
  // Numeric id normalisation: HTML <select> emits e.target.value as a string.
  // These fields must be numbers before CT switch statements and list lookups run.
  // Rule: non-null value → Number(); null/undefined → untouched.
  const stepTypeIdNum = Number(ecStep.stepTypeId) as CTBranchStep['stepTypeId'];
  const characterIdNum = ecStep.characterId != null ? Number(ecStep.characterId) : ecStep.characterId;
  const characterExpressionIdNum = ecStep.characterExpressionId != null
    ? Number(ecStep.characterExpressionId)
    : ecStep.characterExpressionId;

  const ctStep: CTBranchStepNullable = {
    id: ecStep.id,
    index: null,
    stepTypeId: stepTypeIdNum,
    // subType: HTTP etalon step.getData() (step.js:52) serialises this field directly.
    // A provides it at getBranchesRaw.js:705 — copy through, not synthesize.
    subType: ecStep.subType ?? null,
    // text ?? null: HTTP etalon (public_preview.js) returns null when field absent.
    text: ecStep.text ?? null,
    characterId: characterIdNum ?? null,
    // characterRelationship: HTTP etalon step.getData() (step.js:55) serialises this field directly.
    // A provides it at getBranchesRaw.js:706 — copy through.
    characterRelationship: ecStep.characterRelationship ?? null,
    // characterExpressionId ?? null: HTTP etalon returns null when field absent (task #8 parity).
    characterExpressionId: characterExpressionIdNum ?? null,
    characterPlacementSide: ecStep.characterPlacementSide ?? 'default',
    // currencyToggleSettings: HTTP etalon step.getData() (step.js:62) serialises this field directly.
    // A provides it at getBranchesRaw.js:707 — copy through.
    currencyToggleSettings: ecStep.currencyToggleSettings ?? null,
    branchId: ecStep.branchId,
    // data is set below — either LEGACY skin-properties (if character present)
    // or raw StepDataRows, mirroring public_preview.js:185-208.
    data: ecStep.data as CTBranchStep['data'],
    answers: (ecStep.answers ?? []).map((a) => transformAnswer(a, answerTypes, modifierTypes)),
    wordCount: ecStep.wordCount ?? 0,
  };

  // speakerMemoryId: EC has number|undefined, CT expects string|undefined
  if (ecStep.speakerMemoryId !== undefined) {
    ctStep.speakerMemoryId = String(ecStep.speakerMemoryId);
  }

  // ---------------------------------------------------------------------------
  // Character + expression client-side resolve
  // Mirrors public_preview.js:194-212 but uses the already-loaded
  // characters list and global expressions list instead of Sequelize includes.
  // ---------------------------------------------------------------------------
  if (characterIdNum != null) {
    const char = characters.find((c) => c.id === characterIdNum);
    if (char) {
      // step.character: full object with imageUrl (matches Character.getData() + imageUrl
      // from server). EC Character from fetchCharactersByBookId already has imageUrl,
      // imageName, properties. The runtime shape is richer than the EC type — cast to
      // CharacterWithRuntimeFields (declared in this file) which makes the extra fields typed.
      ctStep.character = char as unknown as CTBranchStep['character'];

      // LEGACY data overwrite (public_preview.js:206):
      // Replaces StepDataRows with character skin-properties for backward compat.
      const charTyped = char as CharacterWithRuntimeFields;
      ctStep.data = charTyped.properties
        ? ({
            properties: charTyped.properties,
            imageName: charTyped.imageName,
            imageUrl: charTyped.imageUrl,
          } as CTBranchStep['data'])
        : ([] as unknown as CTBranchStep['data']);

      // step.stepData: raw StepDataRows kept separately (public_preview.js:207).
      const ecStepTyped = ecStep as ECBranchStep & { stepData?: Array<{ key: string; value: string }> };
      if (ecStepTyped.stepData !== undefined) {
        ctStep.stepData = ecStepTyped.stepData;
      }
    }
  } else if (ecStep.character !== undefined) {
    // Fallback: character already resolved (e.g. future caching) — use as-is
    ctStep.character = ecStep.character as unknown as CTBranchStep['character'];
  }

  // expression resolve: find by characterExpressionId in global expressions list
  // (mirrors public_preview.js:210-212: step.CharacterExpression.getData())
  if (characterExpressionIdNum) {
    const expr = expressions.find((e) => e.id === characterExpressionIdNum);
    if (expr) {
      ctStep.expression = expr as CTBranchStep['expression'];
    }
  } else if (ecStep.expression !== undefined) {
    // Fallback: expression already resolved
    ctStep.expression = ecStep.expression;
  }

  // switch — EC StepCheckSwitch[] vs CT's inline shape; values are compatible
  if (ecStep.switch !== undefined) {
    ctStep.switch = ecStep.switch as unknown as CTBranchStep['switch'];
  }

  // check — needed by onGoForwardStack to compute Check-step navigation (StepTypes.Check).
  // Without it the emulator gets check=undefined and cannot navigate past a Check node.
  if (ecStep.check !== undefined) {
    ctStep.check = ecStep.check;
  }

  // roll — needed by onGoForwardStack for Random-step navigation (StepTypes.Random).
  // Without it roll is undefined → switch(roll?.type) hits default → throws.
  if (ecStep.roll !== undefined) {
    ctStep.roll = ecStep.roll;
  }

  // achievementId — Achieve step (StepTypes.Achieve); consumed by CT achievement handler.
  if (ecStep.achievementId !== undefined) {
    ctStep.achievementId = ecStep.achievementId;
  }

  // achievementDescription — companion text for the achievement notification.
  // A provides it via additionalData spread (getBranchesRaw.js:700-701, story_branch.js:293).
  // D includes it via step.getData() additionalData spread (step.js:61, step.js:52-61).
  if (ecStep.achievementDescription !== undefined) {
    ctStep.achievementDescription = ecStep.achievementDescription;
  }

  // audio / stepAudios — step-level audio settings; consumed by gameplay-renderer audio layer.
  if (ecStep.audio !== undefined) {
    ctStep.audio = ecStep.audio;
  }
  if (ecStep.stepAudios !== undefined) {
    // Resolve stepAudio.Audio from the client-side audios list + audioPath.
    // Logic lives in resolveStepAudios.ts (DRY — shared with useStepAudioSubscriber).
    // HTTP etalon (public_preview.js:719-724) includes full Audio via Sequelize eager-load.
    // getAllV3 (getBranchesRaw) omits Audio — stepAudios are flat; we rebuild it here.
    // audios[].audioUrl is FULL (bucket + path + userId/type/name) — do NOT pass it as-is
    // or AudioController will double-suffix the path → broken URL.
    ctStep.stepAudios = resolveStepAudios(
      ecStep.stepAudios,
      audios,
      audioPath,
    ) as CTBranchStepNullable['stepAudios'];
  }

  // action — cast through unknown for enum mismatch
  if (ecStep.action !== undefined) {
    ctStep.action = ecStep.action as unknown as CTBranchStep['action'];
  }

  if (ecStep.video !== undefined) {
    ctStep.video = ecStep.video;
  }

  // Hub settings — id/stepId forwarded from A (rowToHubSettings:836), progressBars resolved
  if (ecStep.hubSettings !== undefined) {
    ctStep.hubSettings = {
      // id/stepId: A-source provides them (rowToHubSettings:836); forwarded for etalon parity.
      ...(ecStep.hubSettings.id !== undefined && { id: ecStep.hubSettings.id }),
      ...(ecStep.hubSettings.stepId !== undefined && { stepId: ecStep.hubSettings.stepId }),
      header: ecStep.hubSettings.header,
      subheader: ecStep.hubSettings.subheader ?? null,
      ...(ecStep.hubSettings.progressBars != null && {
        progressBars: resolveProgressBars(
          ecStep.hubSettings.progressBars as unknown[],
          maps.variableMap,
        ) ?? undefined,
      }),
    } as unknown as CTBranchStep['hubSettings'];
  }

  // Hub options — resolve iconUrl, tags, score, mods, replay via client-side maps
  if (Array.isArray(ecStep.hubOptions) && ecStep.hubOptions.length > 0) {
    ctStep.hubOptions = (ecStep.hubOptions
      .map((opt) => ({
          // id/stepId: A-source provides them (rowToHubOption:899,901); forwarded for etalon parity.
          ...(opt.id !== undefined && { id: opt.id }),
          ...(opt.stepId !== undefined && { stepId: opt.stepId }),
          uuid: opt.uuid,
          sortOrder: opt.sortOrder,
          viewType: opt.viewType,
          title: opt.title,
          gotoBranchId: opt.gotoBranchId ?? null,
          // gotoBranchTitle: D resolves this server-side from branchMap (public_preview.js:305).
          // A provides gotoBranchId; we resolve title here using the same branchMap from Preview.tsx.
          gotoBranchTitle: _resolveBranchTitle(opt.gotoBranchId ?? null, maps.branchMap),
          // iconId: normalise to number — icon <select> emits string via e.target.value.
          iconId: opt.iconId != null ? Number(opt.iconId) : null,
          iconUrl: opt.iconId != null ? (maps.iconMap[Number(opt.iconId)] ?? null) : null,
          borderColor: opt.borderColor ?? null,
          state: opt.state ?? { state: 'active' as const },
          // score: plain null-coalesce — the whole hubOptions array is cast
          // `as unknown as CTBranchStep['hubOptions']` at the end of this block,
          // so there is no need for a 3-level conditional generic here (KISS).
          score: opt.score ?? null,
          replay: resolveReplay(opt.replay as Record<string, unknown> | null, maps),
          // tags: raw EC shape → CT { top: unknown[], bottom: unknown[] }
          tags: opt.tags
            ? {
                top: (opt.tags.top ?? []) as unknown[],
                bottom: (opt.tags.bottom ?? []) as unknown[],
              }
            : null,
          // mods: raw array forwarded alongside modsResolved (A-source rowToHubOption:912).
          // CT renderer reads mods for condition evaluation; modsResolved is the resolved view.
          mods: opt.mods ?? [],
          // Resolved fields — populated via client-side maps (mirrors prepareStep)
          scoreResolved: resolveScore(opt.score as Parameters<typeof resolveScore>[0], maps.variableMap),
          tagsResolved: resolveTags(opt.tags as Parameters<typeof resolveTags>[0], maps.tagTemplateMap),
          modsResolved: resolveMods(opt.mods as Parameters<typeof resolveMods>[0], maps),
        }))
      .sort((a, b) => a.sortOrder - b.sortOrder)) as unknown as CTBranchStep['hubOptions'];
  }

  return ctStep;
}

// ---------------------------------------------------------------------------
// Branch transformer — public API
// ---------------------------------------------------------------------------

/**
 * Transforms an editor-client Branch (with steps loaded) into CTBranchDetails.
 * Returns undefined if the branch has no id (safety guard against partial data).
 *
 * @param branch - Editor-client Branch with steps (from AddBranch.state)
 * @param characters - Characters from fetchCharactersByBookId (for avatar resolve)
 * @param expressions - Global expression list from GET /v1/characters/expressions
 * @param audios - Audio list from AudiosProvider context (for stepAudio.Audio resolve)
 * @param audioPath - Base S3 bucket URL (e.g. "https://bucket/audio/custom/") from
 *   branchData.audioPath; used to build Audio.audioUrl matching HTTP public_preview etalon
 *
 * Note: `BranchDetails` is not in CT's public dist/index.d.ts surface.
 * The returned type is `CTBranchDetails` (local mirror). Preview.tsx passes
 * it into the provideBranchDetails callback which is typed as returning
 * `BranchDetails | undefined` in CT's IExternalCallbacks — these shapes are
 * structurally identical so TypeScript structural typing accepts the assignment.
 */
export function convertBranchToEmulator(
  branch: Branch,
  characters: Character[] = [],
  expressions: CharacterExpression[] = [],
  maps: HubResolveMaps = EMPTY_MAPS,
  audios: ECAudio[] = [],
  audioPath: string | null = null,
  answerTypes: AnswerTypeItem[] = [],
  modifierTypes: ModifierTypeItem[] = [],
): CTBranchDetails | undefined {
  if (!branch?.id) {
    return undefined;
  }

  return {
    id: branch.id,
    uuid: branch.uuid,
    title: branch.title ?? null,
    description: branch.description ?? null,
    gotoBranchId: branch.gotoBranchId ?? null,
    x: branch.x ?? 0,
    y: branch.y ?? 0,
    locationId: branch.locationId ?? null,
    bgColor: null,
    pos: null,
    location: branch.location as CTBranchDetails['location'],
    // transformStep returns CTBranchStepNullable (text/characterExpressionId: T|null).
    // CTBranchDetails.steps is CTBranchStep[] — one boundary cast here instead of
    // two per-field `as unknown` casts inside transformStep (NIT-2, task #8).
    steps: (branch.steps ?? []).map(
      (step) => transformStep(step, characters, expressions, maps, audios, audioPath, answerTypes, modifierTypes),
    ) as unknown as CTBranchStep[],
    links_from: undefined,
    links_to: undefined,
    stepTypeMode: branch.stepTypeMode,
  };
}
